import { api, desenvolver } from '../api';
import type {
  AltaSistema,
  CrearSistemaRequest,
  ModificarSistemaRequest,
  RotacionKey,
  Sistema,
} from '../api';

/**
 * Las ocho llamadas de `/api/v1/admin/sistemas`, y nada más (KAN-144).
 *
 * **Ningún tipo se escribe acá.** `Sistema`, `AltaSistema` y `RotacionKey` son
 * alias de `schema.d.ts`, que sale de `docs/openapi.yaml`.
 *
 * # LO QUE ESTOS TIPOS DICEN Y NO HAY QUE PERDER DE VISTA
 *
 * `Sistema` —lo que devuelven el listado, el detalle y todas las escrituras
 * salvo dos— **no tiene campo `apiKey`**. Ni la key ni su hash: la base guarda
 * solo el SHA-256 y un prefijo de 12 caracteres. La omisión es **estructural**,
 * no un descuido: no hay forma de que un `GET` filtre la key porque no existe
 * dónde ponerla.
 *
 * Los dos únicos tipos con `apiKey` en claro son {@link crearSistema}
 * (`AltaSistema`) y {@link rotarKey} (`RotacionKey`). Que sean tipos distintos
 * de `Sistema` es lo que hace imposible confundirlos: una pantalla que intente
 * mostrar `sistema.apiKey` no compila.
 */

/** `GET`. Devuelve **activos e inactivos**: la baja es lógica y hay que verla. */
export function listarSistemas(): Promise<Sistema[]> {
  return desenvolver(api.GET('/api/v1/admin/sistemas'));
}

/**
 * `POST`. **Devuelve la API key en claro, y es una de las dos únicas veces que
 * existe fuera de quien la pidió.** Ver {@link AltaSistema}.
 *
 * `tenants` viaja con los ids que la persona eligió por nombre. Ojo con el
 * caveat del contrato: un id inexistente lo rechaza la FK y sale como `500`, con
 * el sistema **ya creado** —el alta y las autorizaciones no comparten
 * transacción—. Por eso los ids salen siempre de la lista de
 * `/api/v1/consultas/tenants` y nunca de algo tipeado.
 */
export function crearSistema(pedido: CrearSistemaRequest): Promise<AltaSistema> {
  return desenvolver(api.POST('/api/v1/admin/sistemas', { body: pedido }));
}

/** `PATCH`. Lo único modificable es el nombre. Sigue siendo único: repetirlo da `409`. */
export function modificarSistema(id: number, cambios: ModificarSistemaRequest): Promise<Sistema> {
  return desenvolver(
    api.PATCH('/api/v1/admin/sistemas/{id}', { params: { path: { id } }, body: cambios }),
  );
}

/**
 * `DELETE`. **Baja lógica, no borrado**: pone `activo = false` y la key deja de
 * autenticar en el acto, pero la fila queda porque los comprobantes emitidos
 * tienen que poder atribuirse al sistema que los pidió. Por eso la función se
 * llama `desactivar`, igual que el botón.
 */
export function desactivarSistema(id: number): Promise<Sistema> {
  return desenvolver(api.DELETE('/api/v1/admin/sistemas/{id}', { params: { path: { id } } }));
}

/**
 * `POST /{id}/activar`. Revierte la baja lógica; es idempotente.
 *
 * ⚠️ **La key vuelve a ser la que tenía**: reactivar NO genera una nueva. Si el
 * sistema se dio de baja porque su key se filtró, hay que rotar además de
 * reactivar — y eso lo dice la pantalla, porque el contrato no lo puede impedir.
 */
export function activarSistema(id: number): Promise<Sistema> {
  return desenvolver(
    api.POST('/api/v1/admin/sistemas/{id}/activar', { params: { path: { id } } }),
  );
}

/**
 * `POST /{id}/rotar-key`. **La otra vez que se ve la key en claro.**
 *
 * Rotar *es* revocar: la fila tiene un solo hash, así que la key anterior deja
 * de autenticar en el mismo movimiento. No hay período de gracia ni convivencia
 * de dos keys — **la app que esté usando la vieja queda fuera de servicio hasta
 * que le configuren la nueva**. Es la respuesta correcta a una key filtrada o
 * perdida, y por eso la pantalla pide confirmación diciendo esa consecuencia.
 */
export function rotarKey(id: number): Promise<RotacionKey> {
  return desenvolver(
    api.POST('/api/v1/admin/sistemas/{id}/rotar-key', { params: { path: { id } } }),
  );
}

/**
 * `PUT /{id}/tenants/{tenantId}`. Agrega la fila de `sistemas_tenants` que
 * habilita a emitir. Idempotente (`ON CONFLICT DO NOTHING`), por eso es `PUT`.
 *
 * `tenantId` es el **id de la fila de tenants, no el CUIT**, y sale siempre de
 * la lista de `/api/v1/consultas/tenants`: nadie lo escribe.
 */
export function autorizarTenant(id: number, tenantId: number): Promise<Sistema> {
  return desenvolver(
    api.PUT('/api/v1/admin/sistemas/{id}/tenants/{tenantId}', {
      params: { path: { id, tenantId } },
    }),
  );
}

/**
 * `DELETE /{id}/tenants/{tenantId}`. Quita la autorización.
 *
 * Esta fila **sí se borra de verdad**: es un permiso, no un registro fiscal. Y
 * es idempotente en su respuesta: revocar algo que no estaba autorizado devuelve
 * `200`, no `404`.
 */
export function revocarTenant(id: number, tenantId: number): Promise<Sistema> {
  return desenvolver(
    api.DELETE('/api/v1/admin/sistemas/{id}/tenants/{tenantId}', {
      params: { path: { id, tenantId } },
    }),
  );
}

/**
 * Arma el cuerpo del alta. `nombre` con `trim()` —el backend hace lo mismo— y
 * `tenants` siempre presente, aunque sea vacío.
 *
 * **La lista vacía se manda explícita y no se omite** para que quede escrito que
 * fue una elección: un sistema sin tenants es válido, se autentica, y no puede
 * emitir para nadie. El contrato trata ausente y vacío igual, así que esto es
 * para quien lea el pedido en un log.
 */
export function altaDeSistema(datos: { nombre: string; tenants: readonly number[] }): CrearSistemaRequest {
  return { nombre: datos.nombre.trim(), tenants: [...datos.tenants] };
}

/**
 * Cambios del `PATCH`, o `null` si no hay ninguno. Mismo criterio que el ABM de
 * usuarios: viaja **solo lo que se tocó**, y sin cambios no se gasta un pedido.
 */
export function cambiosDeSistema(
  original: Pick<Sistema, 'nombre'>,
  editado: { nombre: string },
): ModificarSistemaRequest | null {
  return editado.nombre === original.nombre ? null : { nombre: editado.nombre };
}
